import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Button from '../Button/Button';

export enum Position {
    Right = 'right',
    Left = 'left',
}

interface PopoverProps {
    children: ReactNode;
    item: ReactNode;
    position: Position;
    horizontal?: Position;
    horizontalShift?: string;
    show?: boolean;
    showThanks?: boolean;
    setShow: (show: boolean) => void;
}

const Popover: React.FC<PopoverProps> = ({
    children,
    item,
    position,
    horizontal,
    horizontalShift,
    show = false,
    showThanks = true,
    setShow,
}: PopoverProps) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (itemRef.current) {
            const rect = itemRef.current.getBoundingClientRect();
            setHeight(rect.height);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                show &&
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                !itemRef.current?.contains(event.target as Node)
            ) {
                setShow(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [show, setShow]);

    return (
        <Wrapper>
            <div ref={itemRef}>{item}</div>
            {show && (
                <PopoverBox
                    ref={popoverRef}
                    $position={position}
                    $horizontal={horizontal}
                    $horizontalShift={horizontalShift}
                    $height={height}
                >
                    {children}
                    {showThanks && <Button onClick={() => setShow(false)}>Ok, thanks!</Button>}
                </PopoverBox>
            )}
        </Wrapper>
    );
};

export default Popover;

const Animation = keyframes`
    0% {
		opacity: 0;
	}

	100% {
        opacity: 1;
	}
`;

const Wrapper = styled.div`
    position: relative;
`;

const PopoverBox = styled.div<{
    $position: Position;
    $horizontal?: Position;
    $height: number;
    $horizontalShift?: string;
}>`
    position: fixed;
    white-space: normal;
    background: ${props => props.theme.colors.light};
    border-radius: 1rem;
    padding: 2.5rem;
    z-index: 4;
    margin-top: ${props => props.$height / 2}px;
    width: 38rem;
    transition: 1s;
    box-shadow: rgb(0 0 0 / 30%) 0px 0.5em 3em;
    animation: ${Animation} 1.5s;

    ${props =>
        props.$horizontalShift &&
        css`
            margin-left: ${props.$horizontalShift};
        `}

    ${props =>
        props.$horizontal == Position.Right
            ? css`
                  right: 0;
              `
            : css``}

    &::after {
        content: '';
        width: 0;
        height: 0;
        position: absolute;
        border-left: 3rem solid transparent;
        border-right: 3rem solid transparent;
        border-bottom: 3rem solid white;
        top: -1.5rem;
        ${props => (props.$position == Position.Right ? 'right: 0;' : '')}
        ${props => (props.$position == Position.Left ? 'left: 0;' : '')}
    }
`;
